import { useState, useMemo } from 'react'
import Navbar from '../features/Navbar'
import Footer from '../features/landing/Footer'
import PromoBanner from '../features/PromoBanner'
import PlanCarousel from '../features/pricing/PlanCarousel'
import { pricingPlans } from '../features/pricing/data/pricingPlans.js'
import PlanFeatures from '../features/pricing/PricingFeature.jsx'
import { useAuth } from '../features/auth/AuthContext'
import { FeatureCheckmarkTransparent } from '../assets/FeatureCheckmarkTransparent'

const PRICING_FAQS = [
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. You can cancel your subscription at any time. Your plan remains active until the end of your current billing period.',
  },
  {
    q: 'Can I switch plans later?',
    a: 'Yes. You can upgrade or downgrade your plan at any time. Changes are applied automatically, with prorated billing where applicable.',
  },
  {
    q: 'When will I be billed?',
    a: 'Monthly plans are billed every month. Annual plans are billed upfront.',
  },
]

// Figma 29.09 `pricing` R5 (§6 M10, built as drawn): a 28 ring of white 5% around a 16 #4CBC18 dot
// (`check_45`, the same glyph as FeatureCheckmarkTransparent), text 12/400 black 4 after the ring.
const CheckIcon = () => (
  <span className="shrink-0 flex">
    <FeatureCheckmarkTransparent />
  </span>
)

const TrustBullets = () => (
  // Set #4, Desktop 2: 8px from "Make every deal..." at md+, which Figma 29.09 keeps (rings at
  // y 344, 30 between items). Mobile untouched, he only specified this gap under Desktop.
  <div className="mx-auto w-fit flex flex-row flex-wrap items-center justify-center gap-x-[30px] gap-y-3 mt-6 md:mt-2">
    <div className="flex items-center gap-1">
      <CheckIcon />
      <span className="text-[12px] leading-[20px] text-black">7-day free trial</span>
    </div>
    {/* Микола: hide "Analyze up to 5 properties" on mobile */}
    <div className="hidden sm:flex items-center gap-1">
      <CheckIcon />
      <span className="text-[12px] leading-[20px] text-black">Analyze up to 5 properties</span>
    </div>
    <div className="flex items-center gap-1">
      <CheckIcon />
      <span className="text-[12px] leading-[20px] text-black">No credit card required</span>
    </div>
  </div>
)

const PricingToggle = ({ billing, setBilling }) => (
  // R6 (§6 M10, built as drawn): track border #E3E9E0, inactive label #4F5F4A.
  <div className="inline-flex items-center p-[3px] rounded-full border border-[#E3E9E0] bg-white">
    <button
      onClick={() => setBilling('monthly')}
      className={`h-[40px] px-5 rounded-full text-[14px] font-bold leading-[24px] transition-all duration-200 ${
        billing === 'monthly' ? 'bg-brand-primary text-white' : 'text-[#4F5F4A] hover:text-brand-dark'
      }`}
    >
      Monthly
    </button>
    <button
      onClick={() => setBilling('annual')}
      className={`h-[40px] px-6 rounded-full text-[14px] font-bold leading-[24px] transition-all duration-200 ${
        billing === 'annual' ? 'bg-brand-primary text-white' : 'text-[#4F5F4A] hover:text-brand-dark'
      }`}
    >
      Annual (Save Up To 50%)
    </button>
  </div>
)

const PricingFaq = () => {
  const [openIndex, setOpenIndex] = useState(0)
  const toggle = (i) => setOpenIndex((c) => (c === i ? -1 : i))

  return (
    // Set #4: 72px under the table on mobile. Spec 29.09 R25-R27: 80 under it from md, on the 1200
    // canvas (x 120-1320) and side by side from 1232, like Home's Faq.
    <section className="w-full max-w-canvas mx-auto px-6 md:px-[120px] canvas:px-0 mt-[72px] md:mt-[80px] flex flex-col canvas:flex-row canvas:justify-between gap-8 canvas:gap-12 text-left">
      {/* M2.7: "Common Questions," must not wrap. */}
      <h2 className="w-full canvas:w-[352px] text-[36px] leading-[48px] font-extrabold text-brand-dark canvas:shrink-0 whitespace-nowrap">
        Common Questions,<br />
        <span className="text-brand-accent">Clear Answers</span>
      </h2>

      <div className="w-full canvas:w-[732px] flex flex-col">
        {PRICING_FAQS.map(({ q, a }, i) => {
          // D1.1.1-new: the first question sat 24px lower than the heading beside it, because
            // every row carries the same vertical padding. Dropped on the first row from the
            // canvas width up, where the two columns are actually side by side.
            const isOpen = openIndex === i
          return (
            // R27: rows 24 + 32 + 23 + 1px #AEC49F (80 closed, 128 open with a two-line answer
            // 16 under the question); the open question and its minus are #4AA52A.
            <div key={q} className="border-b border-[#AEC49F] pt-6 pb-[23px] canvas:first:pt-0 flex flex-col gap-4">
              <button
                type="button"
                onClick={() => toggle(i)}
                aria-expanded={isOpen}
                className={`flex items-center justify-between text-left ${
                  isOpen ? 'text-brand-accent' : 'text-brand-dark'
                }`}
              >
                <span className="text-[20px] leading-[32px] font-bold">{q}</span>
                <span className="w-[18px] shrink-0 flex justify-start">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M0 8h16" />
                    {!isOpen && <path d="M8 0v16" />}
                  </svg>
                </span>
              </button>
              {isOpen && <p className="text-[16px] leading-[28px] text-brand-dark">{a}</p>}
            </div>
          )
        })}
      </div>
    </section>
  )
}

const Pricing = () => {
  const [billing, setBilling] = useState('annual')
  // M2.2: the swiped-to card and the table's mobile column are now one piece of state,
  // so it lives here rather than inside PlanFeatures. Starts on the first card because
  // that is the one the carousel opens on — pre-scrolling to Pro would hide the fact
  // that there is a card to the left of it.
  const [selectedPlan, setSelectedPlan] = useState('starter')
  // D2.5.2: desktop only, null whenever the cursor is off the cards.
  const [hoveredPlan, setHoveredPlan] = useState(null)
  const plans = useMemo(() => pricingPlans[billing], [billing])
  const { user } = useAuth()
  const subStatus = user?.subscription?.status
  const isPaidSubscriber = subStatus === 'active' || subStatus === 'past_due'

  return (
    // D2.1: this page used to sit inside PageShell, whose max-w-canvas also capped the
    // navbar, banner and footer at 1200px — so on a wide screen they were the only place
    // in the app where the chrome stopped short of the edges. Home never used the shell;
    // Navbar and Footer already centre their own content, so the wrapper only did harm.
    <div className="w-full bg-brand-pageFill">
      <Navbar />
      <PromoBanner />

      {/* Set #4, Desktop 1 + 3 / Mobile 2: heading 48px from the banner at every width (was
          64px at md — the 48px mobile value was already right); FAQ-to-footer 64px desktop
          (was 120).
          14.08 followup, Mobile 1: FAQ-to-footer raised to 72px on mobile (was 48) — this
          section closes right after PricingFaq, so its own bottom padding is what governs
          that gap; Faq.jsx (Home) is untouched, this is Pricing's own component. */}
      {/* Figma 29.09 (R3): the title's baseline is 263.6, 111.6 under the banner, so its 56px line
          box starts 64 under the banner from md up (§6 M9: the export has no line height for a
          single line; 56 is the live value). */}
      <section className="bg-brand-pageFill pt-[48px] md:pt-[64px] pb-[72px] md:pb-[64px]">
        <div className="max-w-canvas mx-auto px-6 md:px-[120px] flex flex-col items-center text-center">
          <h1 className="text-[36px] leading-[42px] md:text-[56px] md:leading-[56px] tracking-[-1px] md:tracking-normal font-extrabold text-brand-dark">
            Our Pricing Plans
          </h1>

          {/* R4: baseline 326.4, so the 36px line box starts 28 under the title's (§6 M5: the title's
              letter-spacing is the export's 0 — at 0 it measures the drawn 447 wide, x 496-944; the
              live -2px drew it 413). */}
          <p className="mt-4 md:mt-[28px] text-[20px] md:text-[24px] leading-[28px] md:leading-[36px] font-bold text-brand-dark">
            Make every deal a calculated, confident decision.
          </p>

          {/* D2.2: the checkmarks were hidden for anyone signed in, which is why Микола
              could not see them. They are back for visitors and trial users; someone who
              already pays is the one person "7-day free trial / no credit card required"
              would be wrong for, so they stay hidden there. */}
          {!isPaidSubscriber && <TrustBullets />}

          {/* R6: the toggle's track is 28 under the trust row (y 372 -> 400). */}
          <div className="mt-8 md:mt-[28px]">
            <PricingToggle billing={billing} setBilling={setBilling} />
          </div>

          <PlanCarousel
            plans={plans}
            billing={billing}
            onSelect={setSelectedPlan}
            onHover={setHoveredPlan}
          />

          <PlanFeatures selectedPlan={selectedPlan} hoveredPlan={hoveredPlan} />
        </div>

        <PricingFaq />
      </section>

      <Footer />
    </div>
  )
}

export default Pricing
